import React from 'react';
import {
  Accordion,
  AccordionHeader,
  Typography,
  AccordionBody,
  TextField,
  Button,
} from '@one-thd/sui-atomic-components';

const TOTAL_AMOUNT_ACCORDION_CONTENT_ID = 'order-filter-total-amount-content';

const getMinExceedsMaxError = (min: number | null, max: number | null) =>
  min !== null && max !== null && min > max
    ? 'Minimum amount cannot be greater than maximum amount'
    : '';

interface OrderTotalAmountFilterProps {
  minAmount: number | null;
  maxAmount: number | null;
  setMinAmount: (value: number | null) => void;
  setMaxAmount: (value: number | null) => void;
  minAmountError: string;
  maxAmountError: string;
  setMinAmountError: (value: string) => void;
  setMaxAmountError: (value: string) => void;
  setAppliedMinAmount: (value: number | null) => void;
  setAppliedMaxAmount: (value: number | null) => void;
  setFilterChanged: React.Dispatch<React.SetStateAction<boolean>>;
}

export const OrderTotalAmountFilter: React.FC<OrderTotalAmountFilterProps> = ({
  minAmount,
  maxAmount,
  setMinAmount,
  setMaxAmount,
  minAmountError,
  maxAmountError,
  setMinAmountError,
  setMaxAmountError,
  setAppliedMinAmount,
  setAppliedMaxAmount,
  setFilterChanged,
}) => {
  const validateAmount = (amount: number | null, type: 'min' | 'max') => {
    if (type === 'min') {
      const minExceedsMaxError = getMinExceedsMaxError(amount, maxAmount);
      if (minExceedsMaxError) {
        setMinAmountError(minExceedsMaxError);
        setMaxAmountError('');
        return;
      }
    }
    if (
      type === 'max' &&
      amount !== null &&
      minAmount !== null &&
      amount < minAmount
    ) {
      setMaxAmountError('Maximum amount cannot be less than minimum amount');
      setMinAmountError('');
      return;
    }
    setMinAmountError('');
    setMaxAmountError('');
  };

  const hasAmountError = Boolean(minAmountError || maxAmountError);

  return (
    <div className="sui-relative sui-pt-[14px]">
      <Accordion noBorders>
        <AccordionHeader aria-controls={TOTAL_AMOUNT_ACCORDION_CONTENT_ID}>
          Total Amount
        </AccordionHeader>
        <AccordionBody id={TOTAL_AMOUNT_ACCORDION_CONTENT_ID}>
          <div className="sui-mb-[20px]">
            <Typography weight="bold" className="sui-mb-[4px]">
              Enter a custom range:
            </Typography>
          </div>

          <div
            className={`sui-flex sui-items-end sui-gap-[12px] sui-w-full ${
              hasAmountError ? 'sui-mb-[30px]' : ''
            }`}
          >
            <TextField
              type="number"
              aria-label="Minimum Total Amount"
              placeholder="$ Min"
              fullWidth
              value={minAmount ?? ''}
              status={minAmountError ? 'error' : undefined}
              statusMessage={minAmountError ?? undefined}
              FormStatusMessageProps={{
                className:
                  'sui-absolute sui-left-0 sui-w-full sui-font-regular sui-mt-1 sui-text-xs sui-leading-none sui-text-left sui-text-danger',
                style: { top: '100%' },
              }}
              onChange={(event: { target: { value: string } }) => {
                const value =
                  event.target.value === '' ? null : Number(event.target.value);

                setMinAmount(value);
                setMinAmountError('');
                setMaxAmountError('');
              }}
              onBlur={() => {
                validateAmount(minAmount, 'min');
              }}
            />
            <div className="sui-flex sui-items-center sui-self-stretch">
              <Typography> to</Typography>
            </div>
            <TextField
              type="number"
              aria-label="Maximum Total Amount"
              placeholder="$ Max"
              fullWidth
              value={maxAmount ?? ''}
              status={maxAmountError ? 'error' : undefined}
              statusMessage={maxAmountError ?? undefined}
              FormStatusMessageProps={{
                className:
                  'sui-absolute sui-left-0 sui-w-full sui-font-regular sui-mt-1 sui-text-xs sui-leading-none sui-text-left sui-text-danger',
                style: { top: '100%' },
              }}
              onChange={(event: { target: { value: string } }) => {
                const value =
                  event.target.value === '' ? null : Number(event.target.value);
                setMaxAmountError('');
                setMaxAmount(value);
                setMinAmountError('');
              }}
              onBlur={() => {
                validateAmount(maxAmount, 'max');
              }}
            />
            <Button
              variant="tertiary"
              onClick={() => {
                const isMinFilled = minAmount !== null;
                const isMaxFilled = maxAmount !== null;

                if (!isMinFilled && !isMaxFilled) {
                  return;
                }

                if (isMinFilled && !isMaxFilled) {
                  setMaxAmountError('Maximum amount is required');
                  return;
                }

                if (!isMinFilled && isMaxFilled) {
                  setMinAmountError('Minimum amount is required');
                  return;
                }

                const minExceedsMaxError = getMinExceedsMaxError(
                  minAmount,
                  maxAmount,
                );
                if (minExceedsMaxError) {
                  setMinAmountError(minExceedsMaxError);
                  setMaxAmountError('');
                  return;
                }

                if (minAmountError || maxAmountError) {
                  return;
                }

                setAppliedMinAmount(minAmount);
                setAppliedMaxAmount(maxAmount);
                setFilterChanged(true);
              }}
            >
              Apply
            </Button>
          </div>
        </AccordionBody>
      </Accordion>
    </div>
  );
};
